import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';

const NotFound = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleGoHome = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  const handleGoBack = () => {
    if (window.history?.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  // Get current path for debugging
  const currentPath = window.location?.pathname;

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        {/* Error Icon */}
        <div className="mb-8">
          <div className="mx-auto w-32 h-32 bg-red-50 border border-red-200 rounded-full flex items-center justify-center">
            <Icon name="AlertTriangle" size={64} className="text-red-500" />
          </div>
        </div>

        {/* Error Content */}
        <div className="mb-8">
          <h1 className="text-8xl font-bold text-gray-200 mb-4">404</h1>
          <h2 className="text-3xl font-semibold text-gray-900 mb-4">Page Not Found</h2>
          <p className="text-gray-600 text-lg mb-6">
            The requested path <code className="px-2 py-1 bg-gray-100 rounded text-gray-900 font-mono text-sm">{currentPath}</code> could not be found.
          </p>
          <p className="text-gray-500 mb-8">
            This might be due to a mistyped URL, a moved page, or the page doesn't exist yet in the application.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Button
            onClick={handleGoHome}
            size="lg"
            className="w-full sm:w-auto min-w-[200px]"
          >
            <Icon name="Home" size={20} />
            {isAuthenticated ? 'Go to Dashboard' : 'Go to Home'}
          </Button>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="outline"
              onClick={handleGoBack}
              size="lg"
              className="min-w-[140px]"
            >
              <Icon name="ArrowLeft" size={16} />
              Go Back
            </Button>

            <Button
              variant="outline"
              onClick={() => window.location?.reload()}
              size="lg"
              className="min-w-[140px]"
            >
              <Icon name="RefreshCw" size={16} />
              Refresh Page
            </Button>
          </div>
        </div>

        {/* Available Routes Information */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Pages</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
            {/* Public Routes */}
            {!isAuthenticated && [
              { path: '/', label: 'Landing Page' },
              { path: '/login', label: 'Login' },
              { path: '/register', label: 'Register' }
            ]?.map((route) => (
              <button
                key={route?.path}
                onClick={() => navigate(route?.path)}
                className="p-2 text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors text-gray-600 hover:text-gray-900"
              >
                <div className="font-medium text-xs">{route?.label}</div>
                <div className="text-xs opacity-70 font-mono">{route?.path}</div>
              </button>
            ))}
            
            {/* Protected Routes */}
            {isAuthenticated && [
              { path: '/dashboard', label: 'Dashboard' },
              { path: '/sales-hub', label: 'Sales Hub' },
              { path: '/purchase-center', label: 'Purchase Center' },
              { path: '/banking', label: 'Banking' },
              { path: '/invoice-creation', label: 'Create Invoice' },
              { path: '/document-manager', label: 'Documents' },
              { path: '/items-management', label: 'Items' },
              { path: '/reports-dashboard', label: 'Reports' },
              { path: '/time-tracking', label: 'Time Tracking' },
              { path: '/compliance-center', label: 'Compliance' },
              { path: '/accountant-tools', label: 'Accounting' },
              { path: '/organization-settings', label: 'Settings' },
              { path: '/profile-settings', label: 'Profile' }
            ]?.map((route) => (
              <button
                key={route?.path}
                onClick={() => navigate(route?.path)}
                className="p-2 text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors text-gray-600 hover:text-gray-900"
              >
                <div className="font-medium text-xs">{route?.label}</div>
                <div className="text-xs opacity-70 font-mono">{route?.path}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Debug Information */}
        <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Debug Information</h4>
          <div className="text-xs text-gray-600 space-y-1 text-left">
            <div><strong>Requested Path:</strong> {currentPath}</div>
            <div><strong>Authentication Status:</strong> {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}</div>
            <div><strong>Referrer:</strong> {document.referrer || 'Direct access'}</div>
            <div><strong>User Agent:</strong> {navigator.userAgent?.split(' ')?.slice(0, 3)?.join(' ')}...</div>
            <div><strong>Timestamp:</strong> {new Date()?.toLocaleString()}</div>
          </div>
        </div>

        {/* Helpful Links */}
        <div className="mt-8">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Quick Navigation</h4>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { path: '/', label: 'Home' },
              { path: '/dashboard', label: 'Dashboard', protected: true },
              { path: '/login', label: 'Login', public: true },
              { path: '/register', label: 'Register', public: true }
            ]?.filter(link => 
              (link?.protected && isAuthenticated) || 
              (link?.public && !isAuthenticated) || 
              (!link?.protected && !link?.public)
            )?.map((link) => (
              <button
                key={link?.path}
                onClick={() => navigate(link?.path)}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
              >
                {link?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8">
          <p className="text-sm text-gray-500">
            If you continue to experience issues, please contact support or check if the URL is correct.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;